import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import * as cartApi from '../../api/cartApi';
import couponsApi from '../../api/couponsApi';

const Checkout = () => {
  const { user, removeFromCart, addPurchase } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState({});
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [couponList, setCouponList] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [selectedCouponId, setSelectedCouponId] = useState(0);
  const [couponResult, setCouponResult] = useState(null);
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    if (!user) { navigate('/', { replace: true }); return; }
    // Load cart items and initialize selection
    if (user?.cart && user.cart.length > 0) {
      const s = {};
      user.cart.forEach(it => s[String(it.id)] = { selected: true, qty: it.qty || 1 });
      setSelection(s);

      // Load coupons
      cartApi.fetchCoupons(user.id).then(setCouponList).catch(() => setCouponList([]));
    }
  }, [user, navigate]);

  if (!user) return null;

  if (!user?.cart || user.cart.length === 0) {
    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <button onClick={() => navigate('/profile/cart')} className="back-button">← חזרה לסל קניות</button>
          <h3>לקופה</h3>
        </div>
        <div className="checkout-empty">
          <div className="empty-icon">🛒</div>
          <div className="empty-text">הסל ריק</div>
          <button onClick={() => navigate('/profile/cart')} className="empty-action-btn">
            חזרה לסל קניות
          </button>
        </div>
      </div>
    );
  }

  const toggleSelect = (id) => {
    setSelection(prev => ({ ...prev, [String(id)]: { ...(prev[String(id)] || {}), selected: !(prev[String(id)]?.selected) } }));
  };

  const changeQty = (id, qty) => {
    setSelection(prev => ({ ...prev, [String(id)]: { ...(prev[String(id)] || {}), qty: Math.max(1, Number(qty) || 1) } }));
  };

  const selectedItemsArray = () => user.cart
    .filter(it => selection[String(it.id)] && selection[String(it.id)].selected)
    .map(it => ({ ...it, quantity: selection[String(it.id)]?.qty || 1 }));

  const handleCheckCoupon = async (couponCode) => {
    setSelectedCoupon(couponCode);
    setCouponResult(null);
    setDiscount(0);
    try {
      const itemIds = selectedItemsArray().map(it => it.id);
      console.log('Checking coupon for items:', couponCode);
      const res = await couponsApi.applyCoupon({ userId: user.id, couponCode , itemIds});
      setCouponResult(res);
      console.log('Coupon check result:', res);
      if (res && res.valid && res.valid.discount) setDiscount(res.valid.discount);
      if (res && res.valid && res.valid.couponId) setSelectedCouponId(res.valid.couponId);
      console.log('Discount set to:', res.valid.discount);
    } catch (e) {
      setCouponResult({ valid: false, message: 'שגיאה בבדיקת קופון' });
      setDiscount(0);
    }
  };

  const handleCreateCheckout = async () => {
    const toBuy = selectedItemsArray();
    if (!toBuy || toBuy.length === 0) return setMessage('בחר/י לפחות פריט אחד לתשלום');
    try {
      setPayLoading(true);
      setMessage(null);
      const orderItems = toBuy.map(it => ({ item_id: it.id, name: it.name, price: it.price, quantity: it.quantity || 1 }));
      
  const res = await cartApi.createOrderAndGetPaymentUrl({
        userId: user.id,
        items: orderItems,
        coupon_id: selectedCouponId,
        coupon_discount: discount
      });
        console.log("Response from createOrderAndGetPaymentUrl:", res);

      if (res && res.paymentUrl) {
        window.open(res.paymentUrl, '_self');
      } else {
        setMessage('לא התקבל כתובת תשלום');
      }
    } catch (err) {
      setMessage((err && err.message) || 'שגיאה בתשלום');
    } finally {
      setPayLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!orderId) return setMessage('אין מזהה הזמנה לאישור');
    try {
      setConfirmLoading(true);
      const res = await cartApi.confirmOrder(user.id, orderId);
      const purchased = res.items || res.order?.items || selectedItemsArray();
      const purchaseRecord = res.order || res;
      if (purchaseRecord) addPurchase(purchaseRecord);
      for (const it of purchased) {
        await removeFromCart(it.id || it.item_id);
      }
      setMessage('תשלום הושלם בהצלחה — תודה! המוצר נוסף להיסטוריית הרכישות');
      setPaymentUrl(null);
      setOrderId(null);
      setSelection({});
      // Navigate back to cart after successful payment
      setTimeout(() => navigate('/profile/cart'), 3000);
    } catch (err) {
      console.error('confirmOrder failed', err);
      setMessage((err && err.message) || 'אימות תשלום נכשל');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <button onClick={() => navigate('/profile/cart')} className="back-button">← חזרה לסל קניות</button>
        <h3>לקופה</h3>
      </div>

      <div className="checkout-panel">
        <h4>בחר/י פריטים לתשלום</h4>

        <div className="checkout-items">
          {user.cart.map(it => {
            const s = selection[String(it.id)] || { selected: false, qty: it.qty || 1 };
            return (
              <div key={`sel-${it.id}`} className="checkout-item">
                <input
                  type="checkbox"
                  checked={!!s.selected}
                  onChange={() => toggleSelect(it.id)}
                />
                <div className="checkout-item-info">
                  <div className="checkout-item-name">{it.name}</div>
                  <div className="checkout-item-price">מחיר יחידה: {it.price} ₪</div>
                </div>
                <div className="checkout-item-quantity">
                  <label>כמות</label>
                  <input
                    type="number"
                    value={s.qty}
                    min={1}
                    onChange={(e) => changeQty(it.id, e.target.value)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="coupon-section">
          <label>
            <select value={selectedCoupon} onChange={e => setSelectedCoupon(e.target.value)}>
              {couponList.map(c => <option key={c.id} value={c.coupon_code}>{c.coupon_code}</option>)}
              <option value="">ללא קופון</option>     
              בחר/י קופון:       
            </select>
          </label>
          <button className="button-apply-coupon" onClick={() => handleCheckCoupon(selectedCoupon)} disabled={!selectedCoupon}>החל קופון</button>
          <p>{couponResult && (
            <span className={couponResult.valid ? 'coupon-valid' : 'coupon-invalid'}>
              {couponResult.message || (couponResult.valid ? `₪הנחה: ${discount} ` : 'קופון לא תקף')}
            </span>
          )}</p>
        </div>

        <div className="checkout-actions">
          <button button type="button" className="pay-btn" onClick={handleCreateCheckout} disabled={payLoading}>
            {payLoading ? 'מייצר בקשת תשלום...' : 'לתשלום'}
          </button>
          <button className="cancel-btn" onClick={() => navigate('/profile/cart')}>
            חזרה לסל
          </button>
        </div>

        {message && <div className="checkout-message">{message}</div>}
      </div>

      {paymentUrl && (
        <div className="payment-section">
          <h4>חלון תשלום</h4>
          <div className="payment-iframe-container">
            <iframe title="payment" src={paymentUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
          </div>
          <div className="payment-actions">
            <button onClick={handleConfirmPayment} disabled={confirmLoading}>{confirmLoading ? 'בודק תשלום...' : 'אימתי תשלום (סיימתי)'}</button>
            <button onClick={() => { setPaymentUrl(null); setOrderId(null); }}>ביטול תשלום</button>
          </div>
          {message && <div className="payment-message">{message}</div>}
        </div>
      )}
    </div>
  );
};

export default Checkout;
